import { toTestId } from '@fluentsms/utils';
import { Grid, makeStyles, TextField, Theme, Typography } from '@material-ui/core';
import { AutocompleteRenderInputParams } from '@material-ui/lab';
import React, { ReactNode } from 'react';
import { AutocompleteInput } from '../../AutocompleteInput';

export type DrawerAutocompleteOptions = {
  options: DrawerOptions[];
  selectedOption: DrawerOptions;
};

export interface DrawerOptions {
  name: string;
  itemId: string;
  selected: boolean;
  description?: ReactNode;
}

export interface DrawerAutocompleteProps {
  options: DrawerOptions[];
  value: DrawerOptions;
  label: string;
  disableClearable?: boolean;
  // eslint-disable-next-line @typescript-eslint/ban-types
  onChange: (event: React.ChangeEvent<{}>, option: DrawerOptions | null) => void;
  renderInput?: (params: AutocompleteRenderInputParams) => JSX.Element;
  disabled?: boolean;
}

const useStyles = makeStyles((theme: Theme) => ({
  spacing: {
    '& > .MuiGrid-item': {
      padding: theme.spacing(0, 1),
    },
    '& > .MuiGrid-item:first-child': {
      paddingTop: theme.spacing(1),
    },
    '& > .MuiGrid-item:last-child': {
      paddingBottom: theme.spacing(1),
    },
  },
  optionDescription: {
    wordBreak: 'break-all',
  },
}));

export const DrawerAutocomplete = ({
  renderInput = (params: AutocompleteRenderInputParams) => <TextField {...params} />,
  disableClearable = false,
  ...props
}: DrawerAutocompleteProps): JSX.Element => {
  const { value, options, disabled } = props;
  const classes = useStyles();
  const formattedOptions = options.map((each) => each.itemId);

  return (
    <AutocompleteInput
      id={toTestId(`create-order-drawer-${props.label}-autocomplete`)}
      options={formattedOptions}
      renderOption={(option) => {
        const fullOption = options.find((each) => each.itemId === option);

        if (fullOption) {
          return (
            <Grid container className={classes.spacing}>
              <Grid item xs={12}>
                <Typography>{fullOption.name}</Typography>
              </Grid>
              {fullOption.description && (
                <Grid item xs={12}>
                  <Typography color="textSecondary" className={classes.optionDescription}>
                    {fullOption.description}
                  </Typography>
                </Grid>
              )}
            </Grid>
          );
        } else {
          return null;
        }
      }}
      getOptionLabel={(option) => options.find((each) => each.itemId === option)?.name ?? ''}
      renderInput={renderInput}
      disableClearable={disableClearable || undefined}
      clearOnEscape={true}
      clearOnBlur={true}
      data-testid={toTestId(`create-order-drawer-${props.label}-autocomplete`)}
      value={value.itemId}
      onChange={(_event, changedItemId) => {
        const item = options.find((each) => each.itemId === changedItemId) ?? null;
        return props.onChange(_event, item);
      }}
      getOptionSelected={(option) => {
        const item = options.find((each) => each.itemId === option) ?? null;
        return Boolean(item && item.selected);
      }}
      ListboxProps={{
        style: {
          maxHeight: '275px',
        },
      }}
      disabled={disabled}
    />
  );
};
